import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { AnswerInput, useProgram } from 'shared/providers/ProgramProvider';
import { GuidanceTextResponse } from 'npo/pages/ReportBuilder/Components/utils';
import {
  BeneficiaryRoutes,
  BeneficiaryRouteType,
} from 'npo/pages/ReportBuilder/Components/Beneficiary/BeneficiaryRoutes';
import { Beneficiaries } from 'npo/pages/ReportBuilder/Components/Beneficiary/utils/beneficiaryInterfaces';
import { BENEFICIARY_CATEGORIES_KEYS } from 'npo/pages/ReportBuilder/Components/Beneficiary/utils/helperFunctions';
import TrueImpactButton from 'shared/design-system/components/TrueImpactButton';
import { transformStringToBeneficiaries } from 'npo/pages/ReportBuilder/Components/Beneficiary/utils/transformStringToBeneficiaries';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';

const emptyGuidance: GuidanceTextResponse = {};
const noop = () => undefined;

const emptyBeneficiaries: Beneficiaries = {
  age: undefined,
  ethnic: undefined,
  gender: undefined,
  health: undefined,
  social: undefined,
  work: undefined,
};

const hasAnyBeneficiary = (b: Beneficiaries): boolean =>
  BENEFICIARY_CATEGORIES_KEYS.some(k => b[k] !== undefined);

// MainCategory hard-codes a 280px bottom margin for the Report Builder layout.
// Neutralize it here so the internal Continue button sits directly under the
// cards in the Beta program-question modal.
const StyledDiv = styled.div`
  &.disabled {
    pointer-events: none;
    opacity: 0.6;
  }
  .beneficiary-main-category {
    margin-bottom: ${SPACERS.medium};
  }
  .continue-row {
    display: flex;
    justify-content: center;
    margin-top: ${SPACERS.large};
    button {
      min-width: 200px;
    }
  }
`;

interface BeneficiaryFieldProps {
  value: AnswerInput;
  onChange: (next: AnswerInput) => void;
  disabled?: boolean;
  onHideFooterChange?: (hide: boolean) => void;
}

// Wraps the ReportBuilder BeneficiaryRoutes for the Beta program-question flow.
// Persistence is a single JSON string on `AnswerInput.text` for Q60001; the
// report-scoped mutation used in ReportBuilder does not apply here.
const BeneficiaryField: React.FC<BeneficiaryFieldProps> = ({
  value,
  onChange,
  disabled,
  onHideFooterChange,
}) => {
  const { programName, answersTextById } = useProgram();

  const [beneficiaries, setBeneficiaries] = useState<Beneficiaries>(() =>
    transformStringToBeneficiaries(emptyBeneficiaries, value.text ?? undefined),
  );
  const [route, setRoute] = useState<BeneficiaryRouteType>(() =>
    hasAnyBeneficiary(
      transformStringToBeneficiaries(
        emptyBeneficiaries,
        value.text ?? undefined,
      ),
    )
      ? 'categories'
      : 'landing',
  );

  // Rehydrate from a late-arriving persisted answer. `value.text` isn't always
  // present on first mount (the parent's `input` can be repopulated after the
  // ProgramProvider patches the questions cache), and useState only runs its
  // initializer once. Only fire while local state is still empty so we never
  // clobber in-progress edits.
  useEffect(() => {
    if (!value.text) return;
    if (hasAnyBeneficiary(beneficiaries)) return;
    const next = transformStringToBeneficiaries(emptyBeneficiaries, value.text);
    if (hasAnyBeneficiary(next)) {
      setBeneficiaries(next);
      setRoute('categories');
    }
  }, [value.text]);

  // Propagate local changes up as JSON text so the parent save flow picks them
  // up. Skip the initial mount to avoid dirtying an unchanged answer.
  const didMount = useRef(false);
  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true;
      return;
    }
    onChange({ ...value, text: JSON.stringify(beneficiaries) });
  }, [beneficiaries]);

  useLayoutEffect(() => {
    onHideFooterChange?.(route === 'landing');
    return () => onHideFooterChange?.(false);
  }, [route, onHideFooterChange]);

  const goBack = () => {
    if (route === 'categories') setRoute('landing');
  };

  return (
    <StyledDiv
      style={disabled ? { pointerEvents: 'none', opacity: 0.6 } : undefined}
    >
      <BeneficiaryRoutes
        route={route}
        guidanceText={emptyGuidance}
        allAnswers={answersTextById}
        loading={false}
        error={undefined}
        refetch={noop}
        beneficiaries={beneficiaries}
        setBeneficiaries={setBeneficiaries}
        goBack={goBack}
        programName={programName}
      />
      {route === 'landing' && hasAnyBeneficiary(beneficiaries) && (
        <div className="continue-row">
          <TrueImpactButton onClick={() => setRoute('categories')}>
            Continue
          </TrueImpactButton>
        </div>
      )}
    </StyledDiv>
  );
};

export default BeneficiaryField;
